import React, { createContext, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import { useAuth } from '@/features/auth/hooks/use-auth'
import type { DexieDatabase } from './local/dexie-db'
import { createDexieDb } from './local/dexie-db'

export const DexieContext = createContext<DexieDatabase | null>(null)

/**
 * The local database while it opens. `blocked`: an upgrade waits for another
 * copy of the app holding the older version to close (a tab left open, frozen
 * in the background on Android); it goes on by itself once that copy lets go.
 * `failed`: it couldn't open.
 */
export type LocalDbStatus =
  | { state: 'opening' | 'blocked' }
  | { state: 'open'; db: DexieDatabase }
  | { state: 'failed'; error: unknown }

const LocalDbStatusContext = createContext<LocalDbStatus>({ state: 'opening' })

export const DexieProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth()
  const [status, setStatus] = useState<LocalDbStatus>({ state: 'opening' })

  useEffect(() => {
    setStatus({ state: 'opening' })
    if (!user?.id) return
    let current = true
    const newDb = createDexieDb(user.id)
    newDb.on('blocked', () => {
      if (!current) return
      setStatus({ state: 'blocked' })
      Sentry.captureMessage('Local database upgrade blocked by another tab', {
        level: 'warning',
        tags: { operation: 'open-local-db', reason: 'blocked' },
      })
    })
    newDb.open().then(
      () => {
        if (current) setStatus({ state: 'open', db: newDb })
      },
      (error: unknown) => {
        if (!current) return
        setStatus({ state: 'failed', error })
        Sentry.captureException(error, {
          tags: { operation: 'open-local-db', reason: 'failed' },
        })
      }
    )
    return () => {
      current = false
      newDb.close()
    }
  }, [user?.id])

  return (
    <LocalDbStatusContext.Provider value={status}>
      <DexieContext.Provider value={status.state === 'open' ? status.db : null}>
        {children}
      </DexieContext.Provider>
    </LocalDbStatusContext.Provider>
  )
}

export function useDexieDb(): DexieDatabase
export function useDexieDb(options: { safe: true }): DexieDatabase | null
export function useDexieDb(options?: { safe?: boolean }) {
  const ctx = useContext(DexieContext)
  if (!ctx && !options?.safe) {
    throw new Error('DexieProvider not initialized')
  }
  return ctx
}

/** How the local database is doing while it opens (see LocalDbStatus). */
export const useLocalDbStatus = () => useContext(LocalDbStatusContext)
